<!-- ABOUTME: User selection panel component for broadcast email recipients -->
<!-- ABOUTME: Manages user checkbox selection with select-all functionality -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 p-4 shadow-sm relative">
    <h3 class="text-sm font-semibold text-gray-900 mb-2">Select Recipients</h3>
    <p class="text-xs text-gray-600 mb-4">
      <strong>Showing: {{ users.length }} users</strong>
      <span v-if="selectedCount > 0" class="ml-2 text-primary">
        ({{ selectedCount }} selected)
      </span>
    </p>

    <div class="border border-gray-200 rounded-md p-3 max-h-96 overflow-y-auto relative">
      <!-- Loading Overlay -->
      <div v-if="isLoading" class="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-md z-10">
        <div class="text-center">
          <div class="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
          <p class="text-xs text-gray-600">Loading users...</p>
        </div>
      </div>

      <!-- Select All -->
      <div class="flex items-center gap-2 pb-3 border-b border-gray-200 mb-3">
        <input
          type="checkbox"
          id="select_all"
          v-model="selectAll"
          @change="toggleSelectAll"
          :disabled="isLoading"
          class="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary disabled:opacity-50">
        <label for="select_all" class="text-sm text-gray-700 cursor-pointer">
          Select All ({{ users.length }})
        </label>
      </div>

      <!-- User List -->
      <div class="space-y-2">
        <div
          v-for="user in users"
          :key="user.id"
          class="flex items-start gap-2">
          <input
            type="checkbox"
            :id="`user_${user.id}`"
            :value="user.id"
            v-model="localSelectedIds"
            @change="handleSelectionChange"
            :disabled="isLoading"
            class="user-checkbox h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary mt-0.5 disabled:opacity-50">
          <label
            :for="`user_${user.id}`"
            class="text-xs text-gray-700 cursor-pointer flex-1">
            {{ user.name }} ({{ user.email }})
          </label>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && users.length === 0" class="text-center py-8 text-gray-500 text-sm">
        No users match the current filters
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  users: {
    type: Array,
    required: true,
    default: () => []
  },
  selectedUserIds: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['selection-changed'])

const localSelectedIds = ref([...props.selectedUserIds])
const selectAll = ref(false)

// Watch for external changes
watch(() => props.selectedUserIds, (newIds) => {
  localSelectedIds.value = [...newIds]
  updateSelectAllState()
}, { deep: true })

// Watch for user list changes (when filters applied)
watch(() => props.users, () => {
  // Remove IDs that are no longer in the user list
  localSelectedIds.value = localSelectedIds.value.filter(id =>
    props.users.some(user => user.id === id)
  )
  updateSelectAllState()
  emit('selection-changed', [...localSelectedIds.value])
}, { deep: true })

const selectedCount = computed(() => localSelectedIds.value.length)

function updateSelectAllState() {
  selectAll.value = props.users.length > 0 && localSelectedIds.value.length === props.users.length
}

function toggleSelectAll() {
  if (selectAll.value) {
    localSelectedIds.value = props.users.map(user => user.id)
  } else {
    localSelectedIds.value = []
  }
  emit('selection-changed', [...localSelectedIds.value])
}

function handleSelectionChange() {
  updateSelectAllState()
  emit('selection-changed', [...localSelectedIds.value])
}
</script>
